import React from 'react';
import baseStyles from './network_status.module.css';
import downloadIcon from './assets/network/download.png';
import uploadIcon from './assets/network/upload.png';
import NetworkStatusMessages from './network_status_messages';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { Link } from 'ui/base/link';
import { BasePath } from 'services/base/page_path';
import { Pangolin } from 'services/accounts/types';
import { observer } from 'mobx-react';

type NetworkStatusStyles = {
  content: string;
  link: string;
  icon: string;
  statusBox: string;
  table: string;
  row: string;
  topCell: string;
  bottomCell: string;
  topRowContent: string;
  bottomRowContent: string;
  labelText: string;
  digits: string;
  deviceConnected: string;
  text: string;
  emptyText: string;
  containerCaret: string;
  caret: string;
};

const themedStyles: ThemedStyles<NetworkStatusStyles> = {
  [Theme.DAYLIGHT]: {
    content: baseStyles.baseContent,
    link: baseStyles.daylightLink,
    icon: baseStyles.icon,
    statusBox: baseStyles.daylightStatusBox,
    table: baseStyles.baseTable,
    row: baseStyles.baseRow,
    topCell: baseStyles.topCell,
    bottomCell: baseStyles.bottomCell,
    topRowContent: baseStyles.topRowContent,
    bottomRowContent: baseStyles.bottomRowContent,
    labelText: baseStyles.baseLabelText,
    digits: baseStyles.baseDigits,
    deviceConnected: baseStyles.baseDeviceConnected,
    text: baseStyles.baseText,
    emptyText: baseStyles.emptyText,
    containerCaret: baseStyles.daylightContainerCaret,
    caret: baseStyles.daylightCaret,
  },
  [Theme.MIDNIGHT]: {
    content: baseStyles.baseContent,
    link: baseStyles.midnightLink,
    icon: baseStyles.icon,
    statusBox: baseStyles.midnightStatusBox,
    table: baseStyles.baseTable,
    row: baseStyles.baseRow,
    topCell: baseStyles.topCell,
    bottomCell: baseStyles.bottomCell,
    topRowContent: baseStyles.topRowContent,
    bottomRowContent: baseStyles.bottomRowContent,
    labelText: baseStyles.baseLabelText,
    digits: baseStyles.baseDigits,
    deviceConnected: baseStyles.baseDeviceConnected,
    text: baseStyles.baseText,
    emptyText: baseStyles.emptyText,
    containerCaret: baseStyles.midnightContainerCaret,
    caret: baseStyles.midnightCaret,
  },
};

type DownloadDetailsProps = {
  pangolin: Pangolin;
};
const DownloadDetails = observer(({ pangolin }: DownloadDetailsProps) => {
  const styles = useStyles(themedStyles);
  return (
    <>
      { !(pangolin.bandwidthDownloadString && pangolin.bandwidthDownloadUnits) ?
        <div className={styles.emptyText}>-</div> :
        (
          <>
            <div className={styles.digits}>
              {pangolin.bandwidthDownloadString}
            </div>
            <div className={styles.text}>
              {pangolin.bandwidthDownloadUnits}
            </div>
          </>
        )
      }
    </>
  );
});


const UploadDetails = observer(({ pangolin }: DownloadDetailsProps) => {
  const styles = useStyles(themedStyles);
  return (
    <>
      { !(pangolin.bandwidthUploadString && pangolin.bandwidthUploadUnits) ?
        <div className={styles.emptyText}>-</div> :
        (
          <>
            <div className={styles.digits}>
              {pangolin.bandwidthUploadString}
            </div>
            <div className={styles.text}>
              {pangolin.bandwidthUploadUnits}
            </div>
          </>
        )
      }
    </>
  );
});

type ConnectedDisplayProps = {
  deviceCount: number;
};
const ConnectedDisplay = observer(({ deviceCount }: ConnectedDisplayProps) => {
  const styles = useStyles(themedStyles);
  return (
    <>
      { deviceCount == null ?
        <div className={styles.emptyText}>-</div> :
        (
          <>
            <div className={styles.digits}>
              {deviceCount}
            </div>
            <div className={styles.text}>
              {NetworkStatusMessages.deviceUnits(deviceCount)}
            </div>
          </>
        )
      }
    </>
  );
});

type NetworkStatusProps = {
  pangolin: Pangolin;
  deviceCount: number;
};

export const NetworkStatus = observer(({ pangolin, deviceCount }: NetworkStatusProps) => {
  const styles = useStyles(themedStyles);
  return (
    <Link path={{ kind: BasePath.DEVICES_INFO, pangolinID: pangolin.id }} className={styles.link}>
      <div className={styles.content}>
        <div className={styles.statusBox}>
          <div className={styles.table}>
            <div className={styles.row}>
              <div className={styles.topCell}>
                <div className={baseStyles.topRowContent}>
                  <img className={styles.icon} src={downloadIcon} alt={NetworkStatusMessages.download()}/>
                  <div className={styles.labelText}>
                    {NetworkStatusMessages.download()}
                  </div>
                </div>
              </div>
              <div className={styles.topCell}>
                <div className={baseStyles.topRowContent}>
                  <img className={styles.icon} src={uploadIcon} alt={NetworkStatusMessages.upload()}/>
                  <div className={styles.labelText}>
                    {NetworkStatusMessages.upload()}
                  </div>
                </div>
              </div>
              <div className={styles.topCell}>
                <div className={baseStyles.topRowContent}>
                  <div className={styles.labelText}>
                    {NetworkStatusMessages.connected()}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.bottomCell}>
                <div className={baseStyles.bottomRowContent}>
                  <DownloadDetails pangolin={pangolin} />
                </div>
              </div>
              <div className={styles.bottomCell}>
                <div className={baseStyles.bottomRowContent}>
                  <UploadDetails pangolin={pangolin} />
                </div>
              </div>
              <div className={styles.bottomCell}>
                <div className={baseStyles.bottomRowContent}>
                  <ConnectedDisplay deviceCount={deviceCount} />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.containerCaret}>
            <div className={styles.caret} />
          </div>
        </div>
      </div>
    </Link>
  );
});
